import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export interface AccountUser {
  /** Stable account id (Clerk's user id). Sync keys the judge's data by this. */
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'judge';
}

export type AccountState =
  | { status: 'disabled' } // no sign-in provider in this build
  | { status: 'idle' } // never signed in on this device: Clerk isn't even loaded
  | { status: 'loading' }
  | { status: 'unavailable' } // offline, or Clerk couldn't load
  | { status: 'signedOut' }
  /** `denied`: signed in, but the account isn't invited. `problem`: something to tell the judge (e.g. server unreachable). */
  | { status: 'signedIn'; user: AccountUser | null; problem: string | null; denied?: boolean };

export interface Account {
  state: AccountState;
  /** The last account the server confirmed on this device, kept so flow opens offline. Null = never, or signed out. */
  known: KnownUser | null;
  signIn: () => void;
  signOut: () => Promise<void>;
  /** Short-lived API token, or null when not signed in. */
  getToken: () => Promise<string | null>;
  /** Headers that authenticate an API call (a bearer token; test builds may use the service token). Null = can't. */
  authHeaders: () => Promise<Record<string, string> | null>;
}

export interface KnownUser extends AccountUser {
  /** When the server last confirmed this account is invited (ms). */
  verifiedAt: number;
}

// Test builds (`vite build --mode e2e`) have no Clerk and sign in from a test identity instead (see testAuth.ts).
// Normal builds never contain that code: `pnpm check:bundle` fails the build if they do.
const E2E = import.meta.env.MODE === 'e2e';
export const CLERK_KEY: string | undefined = E2E ? undefined : import.meta.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY || import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// The verified account, per device. Its presence also means "load Clerk on start"; everyone else never loads it.
const KNOWN = 'flow.user';
const LEGACY_FLAG = 'flow.account'; // before accounts were required: "signed in here once"
const store = {
  read(): KnownUser | null {
    try {
      const v = JSON.parse(localStorage.getItem(KNOWN) ?? 'null') as KnownUser | null;
      return v && typeof v.id === 'string' && typeof v.email === 'string' ? v : null;
    } catch {
      return null;
    }
  },
  write(u: KnownUser | null) {
    try {
      if (u) localStorage.setItem(KNOWN, JSON.stringify(u));
      else localStorage.removeItem(KNOWN);
      localStorage.removeItem(LEGACY_FLAG);
    } catch {
      /* private mode: this session still works */
    }
  },
  legacy() {
    try {
      return localStorage.getItem(LEGACY_FLAG) === '1';
    } catch {
      return false;
    }
  },
};

/**
 * A Google sign-in in progress, for this tab. The sign-in leaves the page and comes back, and Clerk only finishes it
 * (its redirect callback) while its sign-in modal is open. So: 'started' when the judge taps Sign in; on return, Clerk
 * loads at start, and if the sign-in isn't finished the modal reopens once ('resumed') to finish it. Cleared once
 * signed in (or refused, or signed out). Session storage survives the round trip in the same tab and nothing longer.
 */
const PENDING = 'flow.signingIn';
type Pending = 'started' | 'resumed';
const pending = {
  read: (): Pending | null => {
    try {
      const v = sessionStorage.getItem(PENDING);
      return v === 'started' || v === 'resumed' ? v : null;
    } catch {
      return null;
    }
  },
  set: (v: Pending | null) => {
    try {
      if (v) sessionStorage.setItem(PENDING, v);
      else sessionStorage.removeItem(PENDING);
    } catch {
      /* private mode: a second tap on Sign in still works */
    }
  },
};

/** What a sign-in provider (Clerk, or the test one) reports up, and the commands it takes. */
export interface BridgeApi {
  report: (s: AccountState) => void;
  register: (cmds: { signIn: () => void; signOut: () => Promise<void>; getToken: () => Promise<string | null>; authHeaders?: () => Promise<Record<string, string> | null> }) => void;
  wantsSignIn: () => boolean;
}

const ClerkBridge = lazy(() => import('./ClerkBridge'));
const TestBridge = E2E ? lazy(() => import('./testAuth')) : null;

const Ctx = createContext<Account | null>(null);

/**
 * Signing in is required to use flow (invite only). Clerk (and its network calls) loads on start only on devices
 * that signed in before; the splash page loads it when the judge taps Sign in.
 */
export const AccountProvider = ({ children }: { children: ReactNode }) => {
  const [known, setKnown] = useState<KnownUser | null>(store.read);
  const returning = !!known || store.legacy() || pending.read() !== null;
  const [mounted, setMounted] = useState(() => !!CLERK_KEY && returning);
  const [state, setState] = useState<AccountState>(() =>
    TestBridge ? { status: 'loading' } : !CLERK_KEY ? { status: 'disabled' } : returning ? { status: 'loading' } : { status: 'idle' });
  const cmds = useRef<Parameters<BridgeApi['register']>[0] | null>(null);
  const pendingSignIn = useRef(false);

  // Keep the local record in step with what the server says.
  useEffect(() => {
    if (state.status === 'signedIn' && state.user) {
      const u: KnownUser = { ...state.user, verifiedAt: Date.now() };
      store.write(u);
      setKnown(u);
      pending.set(null);
    } else if (state.status === 'signedOut' || (state.status === 'signedIn' && state.denied)) {
      store.write(null);
      setKnown(null);
      // Keep a sign-in that's still finishing (the modal reopened to complete it); a refusal ends it.
      if (state.status === 'signedIn') pending.set(null);
    }
  }, [state]);

  const bridge = useMemo<BridgeApi>(() => ({
    report: setState,
    register: (c) => (cmds.current = c),
    wantsSignIn: () => {
      // Tapped Sign in before Clerk had loaded, or back from Google with the sign-in not finished yet (once).
      const tapped = pendingSignIn.current;
      pendingSignIn.current = false;
      if (tapped) return true;
      if (pending.read() === 'started') {
        pending.set('resumed');
        return true;
      }
      return false;
    },
  }), []);

  const signIn = useCallback(() => {
    pending.set('started');
    if (cmds.current) cmds.current.signIn();
    else if (CLERK_KEY) {
      pendingSignIn.current = true;
      setState({ status: 'loading' });
      setMounted(true);
    }
  }, []);

  const signOut = useCallback(async () => {
    pending.set(null);
    store.write(null);
    setKnown(null);
    await cmds.current?.signOut();
    setState({ status: 'signedOut' });
  }, []);

  const getToken = useCallback(async () => (cmds.current ? cmds.current.getToken() : null), []);
  const authHeaders = useCallback(async () => {
    if (cmds.current?.authHeaders) return cmds.current.authHeaders();
    const token = await getToken();
    return token ? { authorization: `Bearer ${token}` } : null;
  }, [getToken]);

  const value = useMemo(() => ({ state, known, signIn, signOut, getToken, authHeaders }), [state, known, signIn, signOut, getToken, authHeaders]);

  return (
    <Ctx.Provider value={value}>
      {TestBridge && (
        <Suspense fallback={null}>
          <TestBridge api={bridge} />
        </Suspense>
      )}
      {mounted && CLERK_KEY && (
        <Suspense fallback={null}>
          <ClerkBridge publishableKey={CLERK_KEY} api={bridge} />
        </Suspense>
      )}
      {children}
    </Ctx.Provider>
  );
};

export const useAccount = (): Account => {
  const a = useContext(Ctx);
  if (!a) throw new Error('AccountProvider missing');
  return a;
};

export type Access = 'allowed' | 'checking' | 'signedOut' | 'denied';

/**
 * May this device use the app right now?
 * - Confirmed by the server: yes.
 * - Can't check (offline, Clerk or the server unreachable, or still loading): yes if this device has a confirmed
 *   account on record, so flow opens instantly and works offline at tournaments. It re-checks once online.
 * - Signed out, or signed in with an account that isn't invited: no.
 */
export const accessFor = (state: AccountState, known: KnownUser | null): Access => {
  switch (state.status) {
    case 'signedIn':
      if (state.denied) return 'denied';
      return state.user || known ? 'allowed' : 'checking';
    case 'loading':
      return known ? 'allowed' : 'checking';
    case 'unavailable':
      return known ? 'allowed' : 'signedOut';
    default:
      return 'signedOut';
  }
};

export const useAccess = (): Access => {
  const { state, known } = useAccount();
  return accessFor(state, known);
};
